import React from 'react';
import { Form as AntForm, FormProps as AntFormProps, FormItemProps as AntFormItemProps } from 'antd';
import { Form as CompatibleForm } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface FormProps extends AntFormProps {
  className?: string;
}

export interface FormItemProps extends AntFormItemProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  form: {
    backgroundColor: theme.color.semantic.background.neutral.secondary,
    '& .ant-form-item-label > label': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      lineHeight: theme.typography.lineHeight.normal,
      '&.ant-form-item-required': {
        '&::before': {
          color: theme.color.semantic.brand.danger.primary,
        },
      },
    },
    '& .ant-form-item-optional': {
      color: theme.color.semantic.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.tight,
    },
    '& .ant-form-item-explain-error': {
      color: theme.color.semantic.brand.danger.primary,
      fontSize: theme.typography.fontSize.xs,
    },
    '& .ant-form-item-explain-warning': {
      color: theme.color.semantic.accent.orange.strong,
      fontSize: theme.typography.fontSize.xs,
    },
    '& .ant-form-item-explain-success': {
      color: theme.color.semantic.brand.default.primary,
      fontSize: theme.typography.fontSize.xs,
    },
    '& .ant-form-item-has-error': {
      '& .ant-input': {
        borderColor: theme.color.semantic.brand.danger.primary,
      },
      '& .ant-input-number': {
        borderColor: theme.color.semantic.brand.danger.primary,
      },
      '& .ant-select-selector': {
        borderColor: `${theme.color.semantic.brand.danger.primary} !important`,
      },
      '& .ant-picker': {
        borderColor: theme.color.semantic.brand.danger.primary,
      },
    },
    '& .ant-form-item-has-warning': {
      '& .ant-input': {
        borderColor: theme.color.semantic.accent.orange.strong,
      },
      '& .ant-input-number': {
        borderColor: theme.color.semantic.accent.orange.strong,
      },
      '& .ant-select-selector': {
        borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
      },
      '& .ant-picker': {
        borderColor: theme.color.semantic.accent.orange.strong,
      },
    },
    '& .ant-form-item-has-success': {
      '& .ant-input': {
        borderColor: theme.color.semantic.brand.default.primary,
      },
      '& .ant-input-number': {
        borderColor: theme.color.semantic.brand.default.primary,
      },
      '& .ant-select-selector': {
        borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
      '& .ant-picker': {
        borderColor: theme.color.semantic.brand.default.primary,
      },
      '& .ant-form-item-feedback-icon': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-input': {
      fontSize: theme.typography.fontSize.sm,
      borderRadius: theme.radius.sm,
      borderWidth: theme.border.width.sm,
    },
    '& .ant-input:disabled, & .ant-input[disabled]': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      opacity: `${theme.opacity[70]} !important`,
      color: `${theme.color.semantic.text.soft} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      cursor: 'not-allowed !important',
      '&:hover, &:focus, &:active': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        boxShadow: 'none !important',
      },
    },
    '& .ant-select-disabled .ant-select-selector': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      opacity: `${theme.opacity[70]} !important`,
      color: `${theme.color.semantic.text.soft} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      cursor: 'not-allowed !important',
      '&:hover, &:focus, &:active': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        boxShadow: 'none !important',
      },
    },
  },
  formHorizontal: {
    padding: `${theme.spacing[3]} ${theme.spacing[0]}`,
    '& .ant-form-item-label': {
      textAlign: 'right',
    },
  },
  formVertical: {
    padding: `${theme.spacing[3]} ${theme.spacing[0]}`,
    '& .ant-form-item-label': {
      textAlign: 'left',
    },
    '& .ant-form-item-explain': {
      fontSize: theme.typography.fontSize.xs,
      lineHeight: theme.typography.lineHeight.tight,
    },
  },
  formInline: {
    padding: `${theme.spacing[0]} ${theme.spacing[2]}`,
    '& .ant-form-item': {
      marginRight: theme.spacing[2],
    },
    '& .ant-form-item-explain': {
      fontSize: theme.typography.fontSize.xs,
      lineHeight: theme.typography.lineHeight.tight,
    },
  },
  formDisabled: {
    backgroundColor: theme.color.semantic.background.neutral.soft,
    '& .ant-form-item-label > label': {
      color: theme.color.semantic.text.soft,
    },
  },
}));

const FormInner: React.FC<FormProps> = ({
  className = '',
  layout = 'vertical',
  ...rest
}) => {
  const classes = useStyles();

  const getLayoutClass = () => {
    switch (layout) {
      case 'horizontal':
        return classes.formHorizontal;
      case 'inline':
        return classes.formInline;
      case 'vertical':
      default:
        return classes.formVertical;
    }
  };

  const combinedClassName = `${classes.form} ${getLayoutClass()} ${className}`.trim();

  return <AntForm className={combinedClassName} layout={layout} {...rest} />;
};

export const Form = Object.assign(FormInner, {
  Item: AntForm.Item,
  List: AntForm.List,
  ErrorList: AntForm.ErrorList,
  useForm: AntForm.useForm,
  Provider: AntForm.Provider,
  create: CompatibleForm.create,
});

export default Form;
